/**
 * SheetPlannerExpandedSection - Expanded row content for SHEET Crew Planner
 * 
 * EF-SHEET-EXPANDED-LAYOUT-REALIGN v1.0:
 * - Left column: Structured info rows (Project/Shift, Times, Venue/Address, Total, Notes)
 * - Right column: Crew assignment (unchanged)
 * - Same vertical height as original
 */
import { useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { CrewMemberContextMenu } from "./SharedCrewMemberContextMenu";
import { AssignmentStatusBadge, OnboardingOverrideBadge } from "./AssignmentStatusBadge";
import { IdPackButton } from "./IdPackButton";
import { ShiftSummaryButton } from "./ShiftSummaryButton";
import {
  User,
  UserPlus,
  Save,
  X,
  Check,
  AlertCircle,
  Users,
  Copy,
  ClipboardPaste,
} from "lucide-react";
import { cn } from "../../lib/utils";

export function SheetPlannerExpandedSection({
  shift,
  projectName,
  venueName,
  locationName,
  crew,
  lineItems,
  venueLocations,
  localEdits,
  onLocalEdit,
  onSave,
  onCancelEdit,
  onAddCrew,
  onRemoveCrew,
  onToggleCrewChief,
  onToggleConfirmed,
  onToggleWorked,
  onSendPWALink,
  onCopyPWAInstall,
  onEmailCrew,
  onPushCrew,
  onEditCrew,
  onCopyCrew,
  onPasteCrew,
  hasClipboard,
  hasChanges,
  scale = 1,
}) {
  // Droppable zone for crew
  const { setNodeRef, isOver } = useDroppable({
    id: `expanded-drop-${shift.id}`,
    data: { type: "shift-drop", shiftId: shift.id },
  });

  // Get display values
  const staffRequired = localEdits.staff_required ?? shift.staff_required ?? 1;
  const notes = localEdits.notes ?? shift.notes ?? "";

  // Crew calculations
  const isCrewChief = (assignment) => {
    return (
      assignment.role?.toLowerCase().includes("crew chief") ||
      assignment.role?.toLowerCase().includes("team leader")
    );
  };

  const crewChiefCount = crew.filter(isCrewChief).length;
  const assignedCount = crew.length;
  const emptySlots = Math.max(0, staffRequired - assignedCount);

  // Shift Total from existing line items (same logic as ShiftLineItems.jsx footer)
  // Source: lineItems from getShiftLineItems() - the normalized dataset used in Edit Shift and Quote
  const shiftTotal = useMemo(() => {
    const items = Array.isArray(lineItems) ? lineItems : [];
    if (items.length === 0) return 0;
    
    // Primary items: Crew-related line items (type_snapshot === 'primary')
    const primaryItems = items.filter(item => 
      item.type_snapshot === 'primary' || item.is_primary
    );
    
    // Supplement items: All other line items (OOT, meals, etc.)
    const supplementItems = items.filter(item => 
      item.type_snapshot !== 'primary' && !item.is_primary
    );
    
    // Primary total: qty × rate × duration (using item's duration_hours)
    const primaryTotal = primaryItems.reduce((acc, item) => {
      const qty = item.quantity || 1;
      const rate = item.unit_rate || 0;
      const duration = item.duration_hours || shift.duration_hours || 0;
      return acc + (qty * rate * duration);
    }, 0);
    
    // Associated total: Use computed_total from supplements
    const associatedTotal = supplementItems.reduce((acc, item) => {
      return acc + (item.computed_total || 0);
    }, 0);
    
    return primaryTotal + associatedTotal;
  }, [lineItems, shift.duration_hours]);

  // Get full address from current location
  const currentLocation = venueLocations.find(loc => loc.id === shift.venue_location_id);
  const fullAddress = currentLocation 
    ? [currentLocation.address1, currentLocation.address2, currentLocation.postcode].filter(Boolean).join(", ")
    : "—";

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "€0.00";
    return `€${amount.toFixed(2)}`;
  };

  // Sizes based on zoom
  const fontSize = Math.round(10 * scale);
  const smallFontSize = Math.round(9 * scale);
  const labelFontSize = Math.round(9 * scale);
  // EVENTSFLO-CREW-PLANNER-CREW-PICKER-SURNAME-SORT-AND-ASSIGNED-LIST-SCROLL-FIX-V1
  // Bumped from 100 → 180 so ~10 assigned-crew rows (≈18px each) are visible
  // before scroll engages. Combined with the native overflow-y-auto switch
  // below, this guarantees admins can see and interact with all assigned
  // crew on 9/9 shifts without the Radix ScrollArea h-full quirk hiding rows.
  const maxCrewHeight = Math.round(180 * scale);

  // Handle crew name click
  const handleCrewNameClick = (e, staffId) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEditCrew) {
      onEditCrew(staffId);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "border-t border-l-4 border-l-primary/40 border-border/30 bg-primary/5 px-2 py-1.5",
        isOver && "bg-primary/10"
      )}
      data-testid={`expanded-section-${shift.id}`}
      style={{ fontSize: `${fontSize}px` }}
    >
      <div className="grid grid-cols-2 gap-3">
        {/* LEFT COLUMN: Structured Info Rows */}
        <div className="space-y-1.5">
          
          {/* Row 1: Project Name, Shift Name */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground" style={{ fontSize: `${labelFontSize}px` }}>Project:</span>
              <span className="font-medium truncate" style={{ maxWidth: `${120 * scale}px` }} title={projectName}>
                {projectName || "—"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground" style={{ fontSize: `${labelFontSize}px` }}>Shift:</span>
              <span className="font-medium truncate" style={{ maxWidth: `${100 * scale}px` }} title={shift.name}>
                {shift.name || "—"}
              </span>
            </div>
          </div>

          {/* Row 2: Date | Start | Duration | End | Crew */}
          <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: `${smallFontSize}px` }}>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Date:</span>
              <span>{formatDate(shift.date)}</span>
            </div>
            <span className="text-border">|</span>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Start:</span>
              <span>{shift.start_time || "—"}</span>
            </div>
            <span className="text-border">|</span>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Duration:</span>
              <span>{shift.duration_hours?.toFixed(1) || "—"} hrs</span>
            </div>
            <span className="text-border">|</span>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">End:</span>
              <span>{shift.end_time || "—"}</span>
            </div>
            <span className="text-border">|</span>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Crew:</span>
              <span className={cn(
                "font-medium",
                assignedCount < staffRequired ? "text-amber-600" : 
                assignedCount > staffRequired ? "text-red-600" : "text-green-600"
              )}>
                {assignedCount}/{staffRequired}
              </span>
            </div>
          </div>

          {/* Row 3: Venue Name + Address */}
          <div style={{ fontSize: `${smallFontSize}px` }}>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Venue:</span>
              <span className="font-medium">{venueName || "—"}</span>
              {shift.out_of_town && (
                <Badge variant="outline" className="text-blue-600 border-blue-600/30 ml-1 px-1 py-0" style={{ fontSize: `${7 * scale}px`, height: `${12 * scale}px` }}>
                  OOT
                </Badge>
              )}
            </div>
            <div className="text-muted-foreground truncate" title={fullAddress}>
              {fullAddress}
            </div>
          </div>

          {/* EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1
             Compact display of the shift-level on-site contact snapshot. */}
          {(shift.on_site_contact_name || shift.on_site_contact_email || shift.on_site_contact_phone) && (
            <div
              style={{ fontSize: `${smallFontSize}px` }}
              data-testid="shift-on-site-contact-display"
            >
              <span className="text-muted-foreground">On-Site Contact:</span>{" "}
              <span className="font-medium">
                {shift.on_site_contact_name || "—"}
              </span>
              {shift.on_site_contact_phone && (
                <span className="text-muted-foreground"> · {shift.on_site_contact_phone}</span>
              )}
              {shift.on_site_contact_email && (
                <span className="text-muted-foreground"> · {shift.on_site_contact_email}</span>
              )}
            </div>
          )}

          {/* EF-SHEET-UX-POLISH-PASS-01: Cost Summary - Reordered to match Quote hierarchy */}
          {/* Row 4: Primary Crew Cost with breakdown */}
          {(() => {
            const items = Array.isArray(lineItems) ? lineItems : [];
            const primaryItems = items.filter(item => 
              item.type_snapshot === 'primary' || item.is_primary
            );
            const primaryTotal = primaryItems.reduce((acc, item) => {
              const qty = item.quantity || 1;
              const rate = item.unit_rate || 0;
              const duration = item.duration_hours || shift.duration_hours || 0;
              return acc + (qty * rate * duration);
            }, 0);
            
            // Get primary item details for breakdown display
            const primaryItem = primaryItems[0];
            const crewCount = primaryItem?.quantity || shift.staff_required || 1;
            const hourRate = primaryItem?.unit_rate || 0;
            const hours = primaryItem?.duration_hours || shift.duration_hours || 0;
            
            return (
              <div className="flex items-center justify-between" style={{ fontSize: `${smallFontSize}px` }}>
                <span className="text-muted-foreground">
                  Primary Crew Cost: <span className="text-foreground/60">({crewCount} × {formatCurrency(hourRate)} × {hours}h)</span>
                </span>
                <span className="font-medium">{formatCurrency(primaryTotal)}</span>
              </div>
            );
          })()}

          {/* Row 4b: Associated Costs - All non-primary line items */}
          {(() => {
            const items = Array.isArray(lineItems) ? lineItems : [];
            const associatedItems = items.filter(item => 
              item.type_snapshot !== 'primary' && !item.is_primary
            );
            
            if (associatedItems.length === 0) return null;
            
            // EF-TASKITEM-COST-BREAKDOWN-DISPLAY: Format pricing breakdown
            const formatBreakdown = (item) => {
              const rate = item.unit_rate || 0;
              const qty = item.quantity || 1;
              const hours = item.duration_hours;
              const total = item.computed_total || 0;
              
              if (hours && hours > 0) {
                // Hourly item: €rate × qty × hours
                return `${formatCurrency(rate)} × ${qty} × ${hours}h = ${formatCurrency(total)}`;
              } else {
                // Fixed cost: €rate × qty
                if (qty === 1) {
                  return formatCurrency(total);
                }
                return `${formatCurrency(rate)} × ${qty} = ${formatCurrency(total)}`;
              }
            };
            
            return (
              <div style={{ fontSize: `${smallFontSize}px` }}>
                <div className="text-muted-foreground mb-0.5">Associated Costs:</div>
                <div className="pl-2 space-y-0.5 border-l-2 border-border/30">
                  {associatedItems.map((item, idx) => (
                    <div key={item.id || idx} className="flex items-center justify-between gap-2">
                      <span className="truncate" title={item.name_snapshot}>
                        {item.name_snapshot || "Item"}
                      </span>
                      <span className="shrink-0 font-medium text-muted-foreground" title={formatBreakdown(item)}>
                        {formatBreakdown(item)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Row 4c: Separator + SHIFT TOTAL (final line, mirrors Quote) */}
          <div className="border-t border-border/40 pt-1 mt-1" style={{ fontSize: `${smallFontSize}px` }}>
            <div className="flex items-center justify-between">
              <span className="font-medium">SHIFT TOTAL:</span>
              <span className="font-semibold text-primary">{formatCurrency(shiftTotal)}</span>
            </div>
          </div>

          {/* Row 5: Notes */}
          <div>
            <Label style={{ fontSize: `${labelFontSize}px` }} className="text-muted-foreground">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => onLocalEdit("notes", e.target.value)}
              placeholder="Shift notes..."
              style={{ fontSize: `${smallFontSize}px`, minHeight: `${40 * scale}px` }}
              className="resize-none"
            />
          </div>

          {/* Save/Cancel Buttons */}
          {hasChanges && (
            <div className="flex items-center gap-1 pt-1">
              <Button
                variant="default"
                size="sm"
                onClick={onSave}
                style={{ height: `${20 * scale}px`, fontSize: `${smallFontSize}px` }}
              >
                <Save style={{ width: `${10 * scale}px`, height: `${10 * scale}px`, marginRight: `${3 * scale}px` }} />
                Save Changes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onCancelEdit}
                style={{ height: `${20 * scale}px`, fontSize: `${smallFontSize}px` }}
              >
                <X style={{ width: `${10 * scale}px`, height: `${10 * scale}px`, marginRight: `${3 * scale}px` }} />
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Crew Assignment (unchanged) */}
        <div className="space-y-1">
          <h4 className="text-muted-foreground flex items-center gap-1" style={{ fontSize: `${labelFontSize}px` }}>
            <Users style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} />
            Crew (
            {/* EVENTSFLO-CREW-PLANNER-COPY-PASTE-SHIFT-CREW-V1
                Colour-code the count: amber when under-staffed, red when
                over capacity (visible at-a-glance after a paste that
                pushes past staff_required). */}
            <span
              className={cn(
                assignedCount < staffRequired && "text-amber-600 dark:text-amber-400",
                assignedCount > staffRequired && "text-red-600 dark:text-red-400 font-semibold",
              )}
            >
              {assignedCount}/{staffRequired}
            </span>
            )
            {crewChiefCount > 0 && (
              <Badge variant="outline" style={{ fontSize: `${7 * scale}px`, height: `${12 * scale}px`, marginLeft: `${4 * scale}px` }}>
                {crewChiefCount} Chief
              </Badge>
            )}
            {/* EVENTSFLO-CREW-PLANNER-COPY-PASTE-SHIFT-CREW-V1
                Copy / Paste Crew buttons sit beside Crew (x/y) header, to
                the left of the ID Pack button. Click-stopPropagation
                prevents the row from collapsing. */}
            <span className="ml-auto flex items-center" onClick={(e) => e.stopPropagation()}>
              {onCopyCrew && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onCopyCrew(shift.id); }}
                  disabled={assignedCount === 0}
                  data-testid={`copy-crew-btn-${shift.id}`}
                  title="Copy this shift's assigned crew to the planner clipboard"
                  style={{
                    height: `${18 * scale}px`,
                    fontSize: `${9 * scale}px`,
                    padding: `0 ${6 * scale}px`,
                    marginLeft: `${4 * scale}px`,
                  }}
                >
                  <Copy style={{ width: `${10 * scale}px`, height: `${10 * scale}px`, marginRight: `${3 * scale}px` }} />
                  Copy Crew ({assignedCount})
                </Button>
              )}
              {onPasteCrew && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onPasteCrew(shift.id); }}
                  disabled={!hasClipboard}
                  data-testid={`paste-crew-btn-${shift.id}`}
                  title={hasClipboard ? "Paste copied crew onto this shift" : "Copy crew from another shift first"}
                  style={{
                    height: `${18 * scale}px`,
                    fontSize: `${9 * scale}px`,
                    padding: `0 ${6 * scale}px`,
                    marginLeft: `${4 * scale}px`,
                  }}
                >
                  <ClipboardPaste style={{ width: `${10 * scale}px`, height: `${10 * scale}px`, marginRight: `${3 * scale}px` }} />
                  Paste Crew
                </Button>
              )}
              {assignedCount > 0 && (
                <IdPackButton shiftId={shift.id} scale={scale} />
              )}
              {/* EVENTSFLO-CREW-PLANNER-SEND-SHIFT-PROJECT-SUMMARY-PDF-V1 */}
              <ShiftSummaryButton shiftId={shift.id} scale={scale} />
            </span>
          </h4>

          {/* Assigned Crew - Internal Scroll
              EVENTSFLO-CREW-PLANNER-CREW-PICKER-SURNAME-SORT-AND-ASSIGNED-LIST-SCROLL-FIX-V1
              Switched from Radix <ScrollArea> to a native overflow-y-auto
              container. Radix ScrollArea's Viewport uses h-full, which
              requires an explicit height on the Root — with only maxHeight,
              the Viewport sized to content (no overflow detected) and the
              Root clipped via overflow-hidden, hiding rows on 9/9 crew
              shifts. Native CSS overflow respects maxHeight reliably and
              shows the browser-native scrollbar when needed. */}
          <div
            style={{ maxHeight: `${maxCrewHeight}px` }}
            className="border border-border/30 rounded overflow-y-auto"
            data-testid="planner-assigned-crew-list"
          >
            <div className="p-0.5 space-y-0.5">
              {crew.map((assignment, idx) => {
                // EVENTSFLO-CREW-PLANNER-COPY-PASTE-SHIFT-CREW-V1
                // Crew assigned beyond the shift's `staff_required` are
                // visually flagged as excess after a copy/paste from a
                // larger source shift. The row stays interactive (remove /
                // context menu / drag) — admins can still delete excess
                // crew manually.
                const isExcess = staffRequired > 0 && idx >= staffRequired;
                return (
                <CrewMemberContextMenu
                  key={assignment.id || assignment.staff_id}
                  assignment={assignment}
                  shiftId={shift.id}
                  onEditCrew={onEditCrew}
                  onToggleConfirmed={onToggleConfirmed}
                  onToggleWorked={onToggleWorked}
                  onSendPWALink={onSendPWALink}
                  onCopyPWAInstall={onCopyPWAInstall}
                  onEmailCrew={onEmailCrew}
                  onPushCrew={onPushCrew}
                  onToggleCrewChief={onToggleCrewChief}
                  onRemove={onRemoveCrew}
                  scale={scale}
                >
                  <div
                    className={cn(
                      "flex items-center gap-1 px-1 rounded",
                      "cursor-pointer transition-colors",
                      isExcess
                        ? "bg-red-500/15 dark:bg-red-900/30 border border-red-500/60 hover:border-red-500"
                        : "bg-surface-elevated/40 border border-border/50 hover:border-primary/30",
                    )}
                    title={isExcess ? `Excess crew — beyond shift capacity of ${staffRequired}` : undefined}
                    data-testid={isExcess ? `assigned-crew-row-excess-${assignment.staff_id}` : `assigned-crew-row-${assignment.staff_id}`}
                    style={{ height: `${18 * scale}px`, fontSize: `${smallFontSize}px` }}
                  >
                    {isExcess && (
                      <AlertCircle
                        className="text-red-600 dark:text-red-400 shrink-0"
                        style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }}
                      />
                    )}
                    {/* EVENTSFLO-CREW-PLANNER-ASSIGNMENT-STATUS-ICONS-V1
                        Unified status pill (Assigned / Sent / Pending / Accepted /
                        Rejected / Cancelled / Confirmed / Worked). Replaces the
                        previous trio of separate confirmed / worked / status
                        widgets so admins see one canonical state per chip. */}
                    <AssignmentStatusBadge assignment={assignment} scale={scale} />
                    <OnboardingOverrideBadge assignment={assignment} scale={scale} />

                    {/* Crew Chief indicator */}
                    {isCrewChief(assignment) && (
                      <span className="text-amber-500 shrink-0" style={{ fontSize: `${9 * scale}px` }}>⛑️</span>
                    )}

                    {/* Name - Clickable */}
                    <span 
                      className="truncate flex-1 hover:underline hover:text-primary"
                      onClick={(e) => handleCrewNameClick(e, assignment.staff_id)}
                      title="Click to edit crew profile"
                    >
                      {assignment.staff_name}
                    </span>

                    {/* Role */}
                    <Badge 
                      variant="outline" 
                      className="shrink-0"
                      style={{ fontSize: `${7 * scale}px`, height: `${12 * scale}px`, padding: `0 ${3 * scale}px` }}
                    >
                      {assignment.role
                        ?.replace(/\s*\(Crew Chief\)/gi, "")
                        .replace(/\s*\(Team Leader\)/gi, "")
                        .trim() || "Crew"}
                    </Badge>
                  </div>
                </CrewMemberContextMenu>
                );
              })}

              {/* Empty Slots */}
              {Array.from({ length: emptySlots }).map((_, idx) => (
                <div
                  key={`slot-${idx}`}
                  className={cn(
                    "flex items-center gap-1 px-1 rounded border-2 border-dashed",
                    isOver
                      ? "border-primary/50 bg-primary/5 text-primary"
                      : "border-muted-foreground/20 text-muted-foreground"
                  )}
                  style={{ height: `${18 * scale}px`, fontSize: `${smallFontSize}px` }}
                >
                  <div 
                    className="rounded-full border border-current flex items-center justify-center"
                    style={{ width: `${12 * scale}px`, height: `${12 * scale}px` }}
                  >
                    <UserPlus style={{ width: `${8 * scale}px`, height: `${8 * scale}px` }} />
                  </div>
                  <span>Drop crew</span>
                </div>
              ))}
            </div>
          </div>

          {/* Crew status warnings */}
          {assignedCount < staffRequired && (
            <div className="flex items-center gap-1 text-amber-600" style={{ fontSize: `${smallFontSize}px` }}>
              <AlertCircle style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} />
              <span>{staffRequired - assignedCount} more needed</span>
            </div>
          )}
          {assignedCount > staffRequired && (
            <div className="flex items-center gap-1 text-red-600" style={{ fontSize: `${smallFontSize}px` }}>
              <AlertCircle style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} />
              <span>Over by {assignedCount - staffRequired}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
